import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { customInstance } from "@repo/api-client";

/**
 * The subset of the venue booking-policy the shell needs to gate the no-show output surfaces. Fetched from
 * the same `/booking-policy` endpoint the schedule feature edits and cached under the SAME query key
 * (`["venue", venueId, "services", "policy"]`), so the two share one cache entry and realtime "services"
 * invalidation refreshes both — mirroring how `entitlements.tsx` shares its key with the floor-plan feature.
 * Defining the fetch here (rather than importing `scheduleApi`) keeps the shell free of a feature-module import.
 */
interface BookingPolicySlice {
  guaranteeMode: "OFF" | "NO_SHOW_FEE";
  noShowFeeAmount: number | null;
  noShowFeeCurrency: string | null;
  managerPinConfigured?: boolean;
}

const fetchPolicy = (venueId: string, signal?: AbortSignal): Promise<BookingPolicySlice> =>
  customInstance<BookingPolicySlice>(
    `/api/v1/venues/${encodeURIComponent(venueId)}/booking-policy`,
    {
      method: "GET",
      signal,
    },
  );

/** The booking-policy query, loaded once at shell start so nav + route guards gate without a flash. */
export function useGuaranteePolicyQuery(venueId: string): UseQueryResult<BookingPolicySlice> {
  return useQuery({
    queryKey: ["venue", venueId, "services", "policy"],
    queryFn: ({ signal }) => fetchPolicy(venueId, signal),
    staleTime: 60_000,
    enabled: venueId.length > 0,
  });
}

export interface GuaranteePolicy {
  /** The venue-wide no-show guarantee is on — gates the `guarantees/failed` surface. */
  noShowEnabled: boolean;
  /**
   * The deposit/prepayment capture is live — gates the `deposits` (Acomptes) surface. Always false for now:
   * the capture half is unbuilt, so the Acomptes page stays hidden until it ships (deposit is not yet a mode).
   */
  depositEnabled: boolean;
  /** The configured no-show fee in minor units + currency, when NO_SHOW_FEE is on. */
  noShowFee: { amountMinor: number; currency: string } | null;
}

const Ctx = createContext<GuaranteePolicy | null>(null);

export function GuaranteePolicyProvider({
  policy,
  children,
}: {
  policy: BookingPolicySlice | undefined;
  children: ReactNode;
}) {
  const value = useMemo<GuaranteePolicy>(() => {
    const noShowEnabled = policy?.guaranteeMode === "NO_SHOW_FEE";
    const amount = policy?.noShowFeeAmount;
    const currency = policy?.noShowFeeCurrency;
    return {
      noShowEnabled,
      depositEnabled: false,
      noShowFee:
        noShowEnabled && typeof amount === "number" && typeof currency === "string"
          ? { amountMinor: amount, currency }
          : null,
    };
  }, [policy]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The current venue's guarantee policy. Throws outside a `/v/:venueId` shell (a programming error). */
export function useGuaranteePolicy(): GuaranteePolicy {
  const value = useContext(Ctx);
  if (!value) throw new Error("useGuaranteePolicy() must be used inside a VenueShellLayout");
  return value;
}
